import React, { useEffect, useState } from "react";
import { isEmpty } from "lodash";
import { useLeaveStore } from "../../../config/stores";
import LeaveApplicationDetails from "../MyLeaveApplications/ManageMyLeaveApplications/LeaveApplicationDetails";
import TableTitle from "../../../components/common/TableTitle";
import AntdTable from "../../../components/common/AntdTable";
import { searchTable } from "../../../config/constants";
import dayjs from "dayjs";
import LeaveApplicationStatus, {
  canModifyLeave,
} from "../../../components/administration/leave/LeaveApplicationStatus";
import LeaveApplicationForm from "../MyLeaveApplications/ManageMyLeaveApplications/LeaveApplicationForm";
import TableButtonActions from "../../../components/common/TableButtonActions";

const AllLeaveApplications = () => {
  const [filterTable, setFilterTableNull] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const leaveStore = useLeaveStore();

  useEffect(() => {
    if (isEmpty(leaveStore.leaveApplications)) {
      leaveStore.getLeaveApplications();
    }
  }, []);

  /**
   * TableButtonActions calls viewDetails(record.id, record); staff name link calls handleView(record).
   */
  const handleView = (arg1, arg2) => {
    const record =
      arg2 != null && typeof arg2 === "object" && !Array.isArray(arg2)
        ? arg2
        : arg1;
    const applicationId =
      (record && typeof record === "object" && (record.id || record.Id || record.ID)) ||
      (typeof arg1 === "string" || typeof arg1 === "number" ? arg1 : undefined);

    if (applicationId) {
      leaveStore.setSelectedApplication(
        typeof record === "object" && record ? record : { id: applicationId }
      );
      leaveStore.setShowLeaveApplicationDetails(true);
      leaveStore.getLeaveApplicationDetails(applicationId);
    } else if (record && typeof record === "object") {
      leaveStore.setSelectedApplication(record);
      leaveStore.setShowLeaveApplicationDetails(true);
    }
  };

  const isEditable = (record) => canModifyLeave(record?.status);

  const handleEdit = (record) => {
    leaveStore.setAddingLeaveApplication(true, true, record);
  };

  const handleDelete = (id) => {
    setDeleteId(id);
  };

  const confirmDelete = () => {
    leaveStore.deleteLeaveApplication(deleteId);
    setDeleteId(null);
  };

  const openAddModal = () => {
    leaveStore.setAddingLeaveApplication(true, true, {});
  };

  const search = (value) => {
    const filterTable = searchTable(leaveStore.leaveApplications?.data || [], value);
    setFilterTableNull(filterTable);
  };

  const refreshTable = () => {
    leaveStore.getLeaveApplications();
  };

  if (leaveStore.loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="container-fluid p-2">
      <h5 className="fw-bold">STAFF LEAVE APPLICATIONS</h5>

      {leaveStore.showLeaveApplicationDetails ? (
        <LeaveApplicationDetails />
      ) : (
        <AntdTable
          loading={leaveStore.loading}
          title={() => (
            <TableTitle
              setFilterTableNull={setFilterTableNull}
              refreshTable={refreshTable}
              search={search}
              openAddModal={openAddModal}
            />
          )}
          pageSize={20}
          data={filterTable === null ? leaveStore.leaveApplications?.data || [] : filterTable}
          columns={[
            {
              title: "Staff Details",
              render: (record) => {
                const staffName =
                  [record?.applicant?.first_name, record?.applicant?.other_names]
                    .filter(Boolean)
                    .join(" ")
                    .trim() || "—";
                return (
                  <div>
                    <span className="d-md-none">Staff:</span>
                    <span
                      className="text-primary cursor-pointer"
                      onClick={() => handleView(record)}
                    >
                      {staffName}
                    </span>
                    <br />
                    <div className="d-md-none">
                      <span>
                        Leave Type:{" "}
                        {record.leave_type === "OTHER"
                          ? record.other_leave_type
                          : record.leave_type}
                      </span>
                      <br />
                      <span>
                        Period: {dayjs(record.start_date).format("DD/MM/YYYY")} -{" "}
                        {dayjs(record.end_date).format("DD/MM/YYYY")}
                      </span>
                      <br />
                      <span>Days: {record.number_of_days}</span>
                      <br />
                      <span>Balance: {record.balance_after_leave}</span>
                      <br />
                      <span>
                        Status: <LeaveApplicationStatus status={record.status} />
                      </span>
                    </div>
                  </div>
                );
              },
            },
            {
              title: "Leave Type",
              render: (record) =>
                record.leave_type === "OTHER" ? record.other_leave_type : record.leave_type,
              responsive: ["md"],
            },
            {
              title: "Start Date",
              dataIndex: "start_date",
              key: "start_date",
              render: (date) => dayjs(date).format("DD/MM/YYYY"),
              responsive: ["md"],
            },
            {
              title: "End Date",
              dataIndex: "end_date",
              key: "end_date",
              render: (date) => dayjs(date).format("DD/MM/YYYY"),
              responsive: ["md"],
            },
            {
              title: "Days",
              dataIndex: "number_of_days",
              key: "number_of_days",
              responsive: ["md"],
            },
            {
              title: "Leave Balance",
              dataIndex: "balance_after_leave",
              key: "balance_after_leave",
              responsive: ["md"],
            },
            {
              title: "Contact",
              render: (record) => (
                <div>
                  <div>{record.address_while_on_leave}</div>
                  <div>{record.phone_while_on_leave}</div>
                </div>
              ),
              responsive: ["md"],
            },
            {
              title: "Status",
              dataIndex: "status",
              key: "status",
              render: (status) => <LeaveApplicationStatus status={status} />,
              responsive: ["md"],
            },
            {
              title: "Actions",
              key: "actions",
              responsive: ["md"],
              render: (record) => (
                <TableButtonActions
                  record={record}
                  viewDetails={handleView}
                  updateRecord={isEditable(record) ? handleEdit : null}
                  deleteRecord={isEditable(record) ? handleDelete : null}
                  confirmDelete={confirmDelete}
                  deleteLoading={leaveStore.deleting}
                  deleteId={deleteId}
                />
              ),
            },
          ]}
        />
      )}

      <LeaveApplicationForm />
    </div>
  );
};

export default AllLeaveApplications;
